/**
 * YouTube Download API Client
 *
 * Typed functions for the desktop-only YouTube download endpoints.
 * Endpoints live at /api/v1/youtube/download/* and are only available
 * when running locally (Electron / local FastAPI).
 *
 * RULES:
 * - Use getApiBaseUrl() from urlResolver.ts (RULE #16)
 * - Use handleDirectResponse() from httpUtils.ts (RULE #16)
 * - All functions are typed (RULE #10)
 */

import { getApiBaseUrl } from '@/services/urlResolver';
import { handleDirectResponse, JSON_HEADERS } from '@/utils/httpUtils';

// =============================================================================
// Types
// =============================================================================

export interface ProbedInfo {
  title: string;
  duration_s: number;
  thumbnail_url: string;
  available_buckets: string[];
}

export interface StartDownloadRequest {
  url: string;
  quality: string;
  output_dir: string;
}

export interface StartDownloadResponse {
  job_id: string;
}

export type DownloadJobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface DownloadStatus {
  job_id: string;
  status: DownloadJobStatus;
  progress_percent: number;
  speed: string;
  file_path: string | null;
  title: string;
  duration_s: number;
  file_size_bytes: number;
  quality_used: string;
  error: string | null;
}

export interface CancelDownloadResponse {
  job_id: string;
  status: string;
}

// =============================================================================
// API functions
// =============================================================================

/**
 * Probe a YouTube URL for metadata and available quality buckets.
 * Errors: 400 bad URL, 404 unavailable, 500 yt-dlp failure.
 */
export async function probeYouTubeInfo(url: string): Promise<ProbedInfo> {
  const base = getApiBaseUrl();
  const encoded = encodeURIComponent(url);
  const response = await fetch(`${base}/api/v1/youtube/download/info?url=${encoded}`);
  return handleDirectResponse<ProbedInfo>(response);
}

/**
 * Start a YouTube download job.
 * Returns job_id for subsequent status polling.
 */
export async function startYouTubeDownload(
  req: StartDownloadRequest
): Promise<StartDownloadResponse> {
  const base = getApiBaseUrl();
  const response = await fetch(`${base}/api/v1/youtube/download`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(req),
  });
  return handleDirectResponse<StartDownloadResponse>(response);
}

/**
 * Get the current status of a download job.
 * Returns 404 if the job_id is unknown.
 */
export async function getYouTubeDownloadStatus(jobId: string): Promise<DownloadStatus> {
  const base = getApiBaseUrl();
  const response = await fetch(`${base}/api/v1/youtube/download/${encodeURIComponent(jobId)}`);
  return handleDirectResponse<DownloadStatus>(response);
}

/**
 * Cancel a running or pending download job.
 */
export async function cancelYouTubeDownload(jobId: string): Promise<CancelDownloadResponse> {
  const base = getApiBaseUrl();
  const response = await fetch(`${base}/api/v1/youtube/download/${encodeURIComponent(jobId)}`, {
    method: 'DELETE',
    headers: JSON_HEADERS,
  });
  return handleDirectResponse<CancelDownloadResponse>(response);
}
